import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    LexicalTypeaheadMenuPlugin,
    MenuOption,
    MenuTextMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { Flex } from '@cad-arc';
import { TextNode, $getSelection } from 'lexical';
import { debounce } from 'lodash';
import { EventAutocompleteOptions, EventAutocompleteView, EventResource } from '@mark43/cad-api';
import React, {
    MutableRefObject,
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useState,
} from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import { useApiClient } from '~/api-client';
import {
    EVENT_NUMBER_MENTION_QUERY_DEBOUNCE_MILLISECONDS,
    EVENT_NUMBER_MENTION_QUERY_MIN_LENGTH,
    EVENT_NUMBER_MENTION_REGEX,
} from './constants';
import { $createEventNumberMentionNode } from './mention-nodes';
import styles from './mention.module.css';

// lexical mentions example https://github.com/facebook/lexical/blob/main/packages/lexical-playground/src/plugins/MentionsPlugin/index.tsx
class MentionTypeaheadOption extends MenuOption {
    agencyEventId: number;
    agencyEventNumber: string;
    cfsNumber: string;
    cfsText: string;

    constructor({
        agencyEventId,
        agencyEventNumber,
        cfsNumber,
        cfsText,
    }: {
        agencyEventId: number;
        agencyEventNumber: string;
        cfsNumber: string;
        cfsText: string;
    }) {
        super(agencyEventNumber);
        this.agencyEventId = agencyEventId;
        this.agencyEventNumber = agencyEventNumber;
        this.cfsNumber = cfsNumber;
        this.cfsText = cfsText;
    }
}

function checkForEventNumberMention(text: string, minMatchLength: number): MenuTextMatch | null {
    const match = EVENT_NUMBER_MENTION_REGEX.exec(text);
    // e.g if input = 'abc #23'.
    // then match = ['#23', '#', '23', groups: undefined, index:4, input: 'abc #23']
    if (match !== null) {
        const matchingString = match[2]; // searching for event number only (without #)
        if (matchingString.length >= minMatchLength) {
            return {
                leadOffset: match.index, // ignore leading chars that appears in front of '#'
                matchingString,
                replaceableString: match[0], // replace event number by selected value ('#' not touched)
            };
        }
    }
    return null;
}
function getPossibleQueryMatch(text: string): MenuTextMatch | null {
    return checkForEventNumberMention(text, 1);
}

function MentionsTypeaheadMenuItem({
    index,
    isSelected,
    onClick,
    onMouseEnter,
    option,
}: {
    index: number;
    isSelected: boolean;
    onClick: () => void;
    onMouseEnter: () => void;
    option: MentionTypeaheadOption;
}) {
    return (
        <li
            key={option.key}
            // 'getSelectedMentionItem' is using className 'item selected' when getElementsByClassName.
            // if className changed here, don't forget to update 'getSelectedMentionItem' and 'mention.modules.css'
            className={classnames(styles['item'], { [styles['selected']]: isSelected })}
            ref={option.setRefElement}
            role="option"
            aria-selected={isSelected}
            tabIndex={-1}
            id={'typeahead-item-' + index}
            data-testid={`mention-plugin-option-${index}`}
            onMouseEnter={onMouseEnter}
            onClick={onClick}
        >
            <span className={classnames(styles['option'])}>
                <span className={classnames(styles['left'])}>#{option.agencyEventNumber}</span>
                <span className={classnames(styles['right'])}>
                    {`${option.cfsNumber} - ${option.cfsText}`}
                </span>
            </span>
        </li>
    );
}

type EventAutocompleteActions =
    | { type: 'SET_OPTIONS'; options: MentionTypeaheadOption[] }
    | { type: 'SET_LOADING' }
    | { type: 'SET_ERROR'; error: string };
type EventAutocompleteSelect =
    | { options: MentionTypeaheadOption[]; loading: false; error: undefined }
    | { options: MentionTypeaheadOption[]; loading: true; error: undefined }
    | { options: MentionTypeaheadOption[]; loading: false; error: string };

export function MentionsPlugin({
    inputContainerRef,
    mentionOptionsPortalRef,
    containerBottom,
    autocompleteOptions,
}: {
    inputContainerRef: MutableRefObject<HTMLDivElement | null>;
    mentionOptionsPortalRef: MutableRefObject<HTMLDivElement | null>;
    containerBottom?: number | string;
    autocompleteOptions?: Pick<EventAutocompleteOptions, 'includeClearedEvents'>;
}): JSX.Element | null {
    const [editor] = useLexicalComposerContext();

    const [queryString, setQueryString] = useState<string | null>(null);

    const onSelectOption = useCallback(
        (
            selectedOption: MentionTypeaheadOption,
            nodeToReplace: TextNode | null,
            closeMenu: () => void
        ) => {
            editor.update(() => {
                const mentionNode = $createEventNumberMentionNode(selectedOption.agencyEventNumber);
                if (nodeToReplace) {
                    nodeToReplace.replace(mentionNode);
                }
                mentionNode.select();
                const selection = $getSelection();
                if (selection) {
                    selection.insertText(
                        ` (${selectedOption.cfsNumber} - ${selectedOption.cfsText}) `
                    );
                }
                closeMenu();
            });
        },
        [editor]
    );

    const apiClient = useApiClient();
    const [state, dispatch] = useReducer(
        (
            state: EventAutocompleteSelect,
            action: EventAutocompleteActions
        ): EventAutocompleteSelect => {
            switch (action.type) {
                case 'SET_OPTIONS':
                    return {
                        loading: false,
                        error: undefined,
                        options: action.options,
                    };
                case 'SET_LOADING':
                    return {
                        loading: true,
                        error: undefined,
                        options: [],
                    };
                case 'SET_ERROR':
                    return {
                        loading: false,
                        error: action.error,
                        options: [],
                    };
                default:
                    return state;
            }
        },
        { options: [], loading: false, error: undefined }
    );

    const fetchOptions = useMemo(() => {
        return debounce(async (input: string) => {
            if (!input || input.trim().length < EVENT_NUMBER_MENTION_QUERY_MIN_LENGTH) {
                return;
            }
            try {
                dispatch({ type: 'SET_LOADING' });
                const eventAutocompleteResponse = await apiClient.request<EventResource.GetEventsByAutocomplete>(
                    {
                        method: 'POST',
                        data: {
                            query: input.trim(),
                            searchCallForService: false,
                            includeClearedEvents: autocompleteOptions?.includeClearedEvents ?? true,
                            agencyTypeGlobalAttrIds: [],
                        },
                        url: 'event/autocomplete',
                    }
                );
                if (eventAutocompleteResponse.data) {
                    const eventAutocompleteViews: EventAutocompleteView[] =
                        eventAutocompleteResponse.data.data?.eventAutocompleteViews ?? [];

                    // reverts the order so the newest are shown first (at the bottom of the component selection popup)
                    eventAutocompleteViews.reverse();

                    dispatch({
                        type: 'SET_OPTIONS',
                        options: eventAutocompleteViews.map(
                            (option) => new MentionTypeaheadOption(option)
                        ),
                    });
                } else {
                    dispatch({ type: 'SET_OPTIONS', options: [] });
                }
            } catch (e) {
                dispatch({
                    type: 'SET_ERROR',
                    error: 'Failed to retrieve user options',
                });
            }
        }, EVENT_NUMBER_MENTION_QUERY_DEBOUNCE_MILLISECONDS);
    }, [apiClient, autocompleteOptions?.includeClearedEvents]);

    const { options } = state;

    useEffect(() => {
        if (!queryString) {
            return;
        }
        fetchOptions(queryString);
    }, [queryString, fetchOptions]);

    return (
        <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
            onQueryChange={setQueryString}
            onSelectOption={onSelectOption}
            triggerFn={getPossibleQueryMatch}
            options={options}
            menuRenderFn={(
                anchorElementRef,
                { selectOptionAndCleanUp, selectedIndex, setHighlightedIndex }
            ) => {
                /**
                 * 'anchorElementRef' is a callback parameter of 'menuRenderFn',
                 * when the typeahead menu is trigged, an anchorElement portal will be injected into the bottom of the dom tree.
                 * we have no control of the portal position
                 *
                 * so, instead of using 'anchorElementRef', we add 'inputContainerRef' which is the container where we create the portal (close to input box, so we can adjust css easily).
                 * 'mentionOptionsPortalRef' is an alias of 'inputContainerRef' (if mention option menu is visible, 'mentionOptionsPortalRef' is defined, otherwise, it's null)
                 * so when the mention option menu is triggered in LexicalTypeaheadMenuPlugin, a dummy anchorElement portal will be injected to the bottom of dom,
                 * at the same time, we create a real portal for the mention menu in 'inputContainerRef', and
                 * 'mentionOptionsPortalRef' reference the 'inputContainerRef' so we can interrupt the lexical Enter and Space press for mention option selection.
                 * when the mention option menu is closed, anchorElementRef will be destroyed automatically, then we set 'mentionOptionsPortalRef' to null, so that lexical can
                 * process Enter and Space press normally.
                 */
                mentionOptionsPortalRef.current = anchorElementRef.current
                    ? inputContainerRef.current
                    : null;
                return anchorElementRef.current && options.length
                    ? ReactDOM.createPortal(
                          <Flex
                              data-testid="mention-plugin-options-portal"
                              className={classnames(styles['typeahead-popover'])}
                              position="absolute"
                              bottom={containerBottom}
                              backgroundColor="var(--arc-colors-surface-foreground)"
                              borderColor="var(--arc-colors-border-default)"
                              borderWidth="var(--arc-space-px)"
                              marginRight="var(--arc-space-3)"
                              overflowY="auto"
                          >
                              <ul>
                                  <span
                                      key="menu-header"
                                      tabIndex={-1}
                                      className={classnames(styles['header'])}
                                  >
                                      <span className={classnames(styles['left'])}>Event #</span>
                                      <span className={classnames(styles['right'])}>CFS</span>
                                  </span>
                                  {options.map((option, i: number) => (
                                      <MentionsTypeaheadMenuItem
                                          index={i}
                                          isSelected={selectedIndex === i}
                                          onClick={() => {
                                              setHighlightedIndex(i);
                                              selectOptionAndCleanUp(option);
                                          }}
                                          onMouseEnter={() => {
                                              setHighlightedIndex(i);
                                          }}
                                          key={option.key}
                                          option={option}
                                      />
                                  ))}
                              </ul>
                          </Flex>,
                          inputContainerRef.current ?? anchorElementRef.current
                      )
                    : null;
            }}
        />
    );
}
